import { DateTime } from 'luxon';

export type TimeDisplayMode = 'system' | 'locale' | '12h' | '24h';
export type ResolvedTimeDisplayMode = '12h' | '24h';
export type TimeOverride = '12' | '24';
export type Meridiem = 'AM' | 'PM';

/** Parse partial or full meridiem text (`a`, `p`, `am`, `pm`). */
export function parseMeridiemFromInput(text: string): Meridiem | null {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith('p')) {
    return 'PM';
  }
  if (trimmed.startsWith('a')) {
    return 'AM';
  }
  return null;
}

export function formatMeridiemDisplay(meridiem: Meridiem): string {
  return meridiem;
}

export function parseTimeOverride(
  value: string | null | undefined
): ResolvedTimeDisplayMode | null {
  if (value === '12') {
    return '12h';
  }
  if (value === '24') {
    return '24h';
  }
  return null;
}

/** Resolve 12h vs 24h from explicit mode, app locale, or browser default. */
export function resolveTimeDisplayMode(
  mode: TimeDisplayMode,
  locale: string
): ResolvedTimeDisplayMode {
  if (mode === '12h' || mode === '24h') {
    return mode;
  }
  const intlLocale = mode === 'locale' ? locale : undefined;
  const { hourCycle } = new Intl.DateTimeFormat(intlLocale, {
    hour: 'numeric',
  }).resolvedOptions();
  return hourCycle === 'h11' || hourCycle === 'h12' ? '12h' : '24h';
}

/** Product `timeOverride` wins; otherwise {@link resolveTimeDisplayMode}. */
export function resolveEffectiveTimeDisplayMode(
  mode: TimeDisplayMode,
  locale: string,
  timeOverride: TimeOverride | null
): ResolvedTimeDisplayMode {
  const fromOverride = parseTimeOverride(timeOverride);
  if (fromOverride) {
    return fromOverride;
  }
  return resolveTimeDisplayMode(mode, locale);
}

export function getTimeFormatExample(
  locale: string,
  mode: TimeDisplayMode = 'locale',
  timeOverride: TimeOverride | null = null
): string {
  return resolveEffectiveTimeDisplayMode(mode, locale, timeOverride) === '12h'
    ? '02:30 PM'
    : '14:30';
}

export function getTimeFormatLabel(
  locale: string,
  mode: TimeDisplayMode = 'locale',
  timeOverride: TimeOverride | null = null
): string {
  return resolveEffectiveTimeDisplayMode(mode, locale, timeOverride) === '12h'
    ? '12-hour format'
    : '24-hour format';
}

export function getTimeRequiredErrorMessage(
  locale: string,
  mode: TimeDisplayMode = 'locale',
  timeOverride: TimeOverride | null = null
): string {
  const label = getTimeFormatLabel(locale, mode, timeOverride);
  const example = getTimeFormatExample(locale, mode, timeOverride);
  return `A valid time in ${label} (e.g. ${example}) is required`;
}

export function getTimeInvalidErrorMessage(
  locale: string,
  mode: TimeDisplayMode = 'locale',
  timeOverride: TimeOverride | null = null
): string {
  const label = getTimeFormatLabel(locale, mode, timeOverride);
  const example = getTimeFormatExample(locale, mode, timeOverride);
  return `Please enter a valid time in ${label} (e.g. ${example})`;
}

export function getAllowedMinutes(intervalSeconds: number | null): number[] | null {
  if (intervalSeconds == null) {
    return null;
  }
  const intervalMinutes = intervalSeconds / 60;
  if (60 % intervalMinutes !== 0) {
    return null;
  }
  const divider = 60 / intervalMinutes;
  return Array.from({ length: divider }, (_, index) => index * intervalMinutes);
}

export function snapMinuteToInterval(
  minute: number,
  intervalSeconds: number | null
): number {
  const allowed = getAllowedMinutes(intervalSeconds);
  if (!allowed?.length) {
    return minute;
  }
  const step = intervalSeconds / 60;
  const snapped = Math.round(minute / step) * step;
  if (snapped >= 60) {
    return 0;
  }
  return allowed.includes(snapped)
    ? snapped
    : allowed.reduce((closest, value) =>
        Math.abs(value - minute) < Math.abs(closest - minute) ? value : closest
      );
}

export function isMinuteOnInterval(
  minute: number,
  intervalSeconds: number | null
): boolean {
  const allowed = getAllowedMinutes(intervalSeconds);
  if (!allowed) {
    return true;
  }
  return allowed.includes(minute);
}

export function decomposeDateTime(
  dateTime: DateTime,
  use12Hour: boolean
): { hour: string; minute: string; meridiem: Meridiem } {
  let hour = dateTime.hour;
  let meridiem: Meridiem = 'AM';
  if (use12Hour) {
    meridiem = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) {
      hour = 12;
    }
  }
  return {
    hour: String(hour).padStart(2, '0'),
    minute: String(dateTime.minute).padStart(2, '0'),
    meridiem,
  };
}

/** Normalize 24h-style hour entry when leaving the hour segment in 12h UI mode. */
export function normalize12HourSegmentsOnHourExit(
  hourStr: string,
  meridiemText: string,
): { hour: string; meridiem?: Meridiem } | null {
  const trimmed = hourStr.trim();
  if (!trimmed) {
    return null;
  }
  const hourNum = parseInt(trimmed, 10);
  if (Number.isNaN(hourNum)) {
    return null;
  }

  const inferMeridiem = parseMeridiemFromInput(meridiemText) === null;

  if (hourNum >= 13 && hourNum <= 23) {
    const parts = decomposeDateTime(
      DateTime.now().startOf('day').set({ hour: hourNum }),
      true,
    );
    return {
      hour: parts.hour,
      ...(inferMeridiem ? { meridiem: parts.meridiem } : {}),
    };
  }

  if (hourNum === 0) {
    return {
      hour: '12',
      ...(inferMeridiem ? { meridiem: 'AM' } : {}),
    };
  }

  if (hourNum >= 1 && hourNum <= 11) {
    return {
      hour: String(hourNum).padStart(2, '0'),
      ...(inferMeridiem ? { meridiem: 'AM' } : {}),
    };
  }

  if (hourNum === 12) {
    return {
      hour: '12',
      ...(inferMeridiem ? { meridiem: 'AM' } : {}),
    };
  }

  if (hourNum >= 1 && hourNum <= 12) {
    return { hour: String(hourNum).padStart(2, '0') };
  }

  return null;
}

export function composeDateTime(
  hourStr: string,
  minuteStr: string,
  meridiem: Meridiem,
  use12Hour: boolean
): DateTime | null {
  if (!hourStr.trim() || !minuteStr.trim()) {
    return null;
  }
  const hourNum = parseInt(hourStr, 10);
  const minuteNum = parseInt(minuteStr, 10);
  if (Number.isNaN(hourNum) || Number.isNaN(minuteNum)) {
    return null;
  }
  let hour24 = hourNum;
  if (use12Hour) {
    if (hourNum < 1 || hourNum > 12) {
      return null;
    }
    if (meridiem === 'AM') {
      hour24 = hourNum % 12;
    } else {
      hour24 = hourNum === 12 ? 12 : hourNum + 12;
    }
  } else if (hourNum < 0 || hourNum > 23) {
    return null;
  }
  if (minuteNum < 0 || minuteNum > 59) {
    return null;
  }
  const composed = DateTime.now()
    .startOf('day')
    .set({ hour: hour24, minute: minuteNum, second: 0, millisecond: 0 })
    .startOf('minute');
  return composed.isValid ? composed : null;
}

export function tryParsePastedTime(
  text: string,
  locale: string
): DateTime | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const formats = ['HH:mm', 'H:mm', 't', 'h:mm a', 'hh:mm a'];
  for (const format of formats) {
    const parsed = DateTime.fromFormat(trimmed, format, { locale });
    if (parsed.isValid) {
      return parsed.startOf('minute');
    }
  }
  return null;
}

export function isHourInRange(
  hourStr: string,
  use12Hour: boolean
): boolean {
  if (!hourStr.trim()) {
    return false;
  }
  const hour = parseInt(hourStr, 10);
  if (Number.isNaN(hour)) {
    return false;
  }
  return use12Hour ? hour >= 1 && hour <= 12 : hour >= 0 && hour <= 23;
}

export function isMinuteInRange(minuteStr: string): boolean {
  if (!minuteStr.trim()) {
    return false;
  }
  const minute = parseInt(minuteStr, 10);
  if (Number.isNaN(minute)) {
    return false;
  }
  return minute >= 0 && minute <= 59;
}
